import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import { normalizeValue } from '../../../../../utils/Texts';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './ModulosModal';
import ModuloReorderModal from './ModuloReorderModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Módulos del Sistema";
const endpoint = ENDPOINTS.modulos;
const endpointById = ENDPOINTS.modulosById;
const modelo = Models.moduloSeguridad;

// Mappers for Create/Update payloads
const maps = {
    mapCreate: (f) => ({
        clave: f.clave,
        nombre: f.nombre,
        descripcion: f.descripcion,
        rutaBase: f.rutaBase,
        icono: 'lucide:ca',
        activo: true, // Siempre verdadero por defecto
        orden: parseInt(f.orden || 0, 10),
    }),
    mapUpdate: (id, f) => ({
        idModulo: id,
        clave: f.clave,
        nombre: f.nombre,
        descripcion: f.descripcion,
        rutaBase: f.rutaBase,
        icono: 'lucide:ca',
        activo: true, // Siempre verdadero
        orden: parseInt(f.orden || 0, 10),
    }),
};

export default function ModulosCatalog() {
    const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('SEGURIDAD_MODULO');

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [editData, setEditData] = useState(null);
    const [editingRowId, setEditingRowId] = useState(null);
    const [page, setPage] = useState(1);
    const [reorderModalOpen, setReorderModalOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(endpoint);
            setRows((res.data || []).map((x) => modelo(x)));
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar información');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let result = [...rows];

        if (q) {
            result = result.filter((r) => {
                const clave = (r.clave || '').toLowerCase();
                const nombre = (r.nombre || '').toLowerCase();
                const desc = (r.descripcion || '').toLowerCase();
                const ruta = (r.rutaBase || '').toLowerCase();
                return (
                    clave.includes(q) || nombre.includes(q) || desc.includes(q) || ruta.includes(q)
                );
            });
        }

        result.sort((a, b) => (a.orden || 0) - (b.orden || 0));

        return result;
    }, [rows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
        setEditingRowId(null);
    };

    const handleEdit = (id) => {
        const r = rows.find(x => x.idModulo === id);
        if (!r) {
            triggerAlert('No se encontró la fila en memoria.');
            return;
        }
        setSelectedId(id);
        setEditData(r);
        setModalMode('edit');
        setModalOpen(true);
        setEditingRowId(id);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            setEditingRowId(null);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (payload, mode) => {
        try {
            setLoading(true);
            if (mode === 'new') {
                const maxOrden = rows.reduce((max, r) => Math.max(max, r.orden || 0), 0);
                payload.orden = maxOrden + 1;
                const res = await axios.post(endpoint, maps.mapCreate(payload));
                const newId = res?.data?.idModulo ?? (typeof res?.data === 'number' ? res.data : null);
                if (newId) setEditingRowId(newId);
            } else {
                await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
                setEditingRowId(selectedId);
            }
            setModalOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >{label}</button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>

                    <input
                        className="roles-search"
                        placeholder="Buscar por clave, nombre, ruta..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    {puedeEditar && (
                        <button
                            className="btn-filter"
                            onClick={() => setReorderModalOpen(true)}
                            title="Reordenar módulos"
                        >
                            🔢 Reordenar
                        </button>
                    )}
                    {puedeCrear && <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>}
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Clave</th>
                                    <th>Nombre</th>
                                    <th>Descripción</th>
                                    <th>Ruta Base</th>
                                    {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                                    {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((r) => {
                                    return (
                                        <tr key={r.idModulo}>
                                            <td>{normalizeValue(r.clave)}</td>
                                            <td>{normalizeValue(r.nombre)}</td>
                                            <td>{normalizeValue(r.descripcion)}</td>
                                            <td>{normalizeValue(r.rutaBase)}</td>

                                            {/* 🔽 Botón EDITAR con ícono */}
                                            {puedeEditar && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="rk-iconbtn rk-iconbtn--edit"
                                                        onClick={() => handleEdit(r.idModulo)}
                                                        aria-label="Editar"
                                                        title="Editar"
                                                    >
                                                        <img src={sigmaEdit} alt="" className="rk-ico" />
                                                    </button>
                                                </td>
                                            )}

                                            {/* 🔽 Botón BORRAR con ícono */}
                                            {puedeEliminar && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="rk-iconbtn rk-iconbtn--del"
                                                        onClick={() => {
                                                            setEditingRowId(null);
                                                            const name = `${r.clave} - ${r.nombre}`;
                                                            askDelete(r.idModulo, name);
                                                        }}
                                                        aria-label="Borrar"
                                                        title="Borrar"
                                                    >
                                                        <img src={sigmaDelete} alt="" className="rk-ico" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {!loading && paged.length === 0 && (
                                    <tr>
                                        <td colSpan={4 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
                        {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
                        <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <Modal
                            open={modalOpen}
                            mode={modalMode}
                            data={editData}
                            inline
                            onClose={() => setModalOpen(false)}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </div>

            {reorderModalOpen && (
                <ModuloReorderModal
                    open={reorderModalOpen}
                    onClose={() => setReorderModalOpen(false)}
                    onSuccess={() => {
                        loadData();
                    }}
                />
            )}

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
